import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@mantis-sas/ui";
import { TrendingUp, TrendingDown, DollarSign } from "lucide-react";

const BudgetExecutionChart = ({ data, loading }) => {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Ejecución de Presupuestos</CardTitle>
          <CardDescription>Cargando datos...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Ejecución de Presupuestos</CardTitle>
          <CardDescription>No hay datos disponibles</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            <p>No hay datos de presupuestos disponibles</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Procesar datos para el gráfico
  const chartData = data.map((budget) => {
    const spentAmount = parseFloat(budget.spent_amount || 0);
    const totalAmount = parseFloat(budget.amount || 0);
    const remainingAmount = totalAmount - spentAmount;
    const executionPercentage =
      totalAmount > 0 ? (spentAmount / totalAmount) * 100 : 0;

    return {
      name: budget.name || `Presupuesto ${budget.id?.slice(-4)}`,
      costCenter:
        budget.cost_center_name ||
        budget.cost_center?.name ||
        "Sin centro de costo",
      totalAmount: totalAmount,
      spentAmount: spentAmount,
      remainingAmount: remainingAmount,
      executionPercentage: Math.round(executionPercentage),
      currency: budget.currency || "USD",
      status:
        executionPercentage > 90
          ? "warning"
          : executionPercentage > 100
          ? "danger"
          : "success",
    };
  });

  // Calcular estadísticas generales
  const totalBudget = chartData.reduce(
    (sum, item) => sum + item.totalAmount,
    0
  );
  const totalSpent = chartData.reduce((sum, item) => sum + item.spentAmount, 0);
  const totalRemaining = totalBudget - totalSpent;
  const overallExecution =
    totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;

  
  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-900">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
              Ejecución de Presupuestos
            </CardTitle>
            <CardDescription className="text-slate-600 dark:text-slate-400">
              Presupuesto asignado vs gastado por centro de costo
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Estadísticas generales */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                Total Asignado
              </span>
            </div>
            <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
              {formatCurrency(totalBudget)}
            </p>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <TrendingDown className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                Total Gastado
              </span>
            </div>
            <p className="text-lg font-bold text-orange-700 dark:text-orange-300">
              {formatCurrency(totalSpent)}
            </p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                Restante
              </span>
            </div>
            <p className="text-lg font-bold text-green-700 dark:text-green-300">
              {formatCurrency(totalRemaining)}
            </p>
          </div>
        </div>

        {/* Progress Bars */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-4">
            Presupuesto asignado vs gastado por centro de costo
          </h4>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {chartData.map((budget, index) => {
              const maxAmount = Math.max(
                budget.totalAmount,
                budget.spentAmount
              );
              const assignedWidth =
                maxAmount > 0 ? (budget.totalAmount / maxAmount) * 100 : 0;
              const spentWidth =
                maxAmount > 0 ? (budget.spentAmount / maxAmount) * 100 : 0;

              return (
                <div key={budget.name} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                        {budget.costCenter}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {budget.name}
                      </p>
                    </div>
                    <div className="text-right ml-2">
                      <p className="text-xs font-medium text-slate-900 dark:text-slate-100">
                        {budget.executionPercentage}%
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {formatCurrency(budget.spentAmount, budget.currency)} /{" "}
                        {formatCurrency(budget.totalAmount, budget.currency)}
                      </p>
                    </div>
                  </div>

                  {/* Progress Bar Container */}
                  <div className="relative">
                    {/* Assigned Budget Bar (Background) */}
                    <div className="w-full bg-blue-100 dark:bg-blue-900/30 rounded-full h-3">
                      <div
                        className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${assignedWidth}%` }}
                      ></div>
                    </div>

                    {/* Spent Budget Bar (Overlay) */}
                    <div className="absolute top-0 left-0 w-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all duration-300 ${
                          budget.executionPercentage > 100
                            ? "bg-red-500"
                            : budget.executionPercentage > 90
                            ? "bg-orange-500"
                            : budget.executionPercentage > 75
                            ? "bg-yellow-500"
                            : "bg-green-500"
                        }`}
                        style={{ width: `${spentWidth}%` }}
                      ></div>
                    </div>

                    {/* Legend indicators */}
                    <div className="flex items-center justify-between mt-1 text-xs text-slate-500 dark:text-slate-400">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>Asignado</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            budget.executionPercentage > 100
                              ? "bg-red-500"
                              : budget.executionPercentage > 90
                              ? "bg-orange-500"
                              : budget.executionPercentage > 75
                              ? "bg-yellow-500"
                              : "bg-green-500"
                          }`}
                        ></div>
                        <span>Gastado</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Indicador de ejecución general */}
        <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Ejecución General
            </span>
            <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
              {Math.round(overallExecution)}%
            </span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                overallExecution > 100
                  ? "bg-red-500"
                  : overallExecution > 90
                  ? "bg-orange-500"
                  : overallExecution > 75
                  ? "bg-blue-500"
                  : "bg-green-500"
              }`}
              style={{ width: `${Math.min(overallExecution, 100)}%` }}
            ></div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BudgetExecutionChart;
